/*
 * axios http 请求配置
 * @baseURL 请求 api 地址
 * @headers 头文件设置
 * @timeout 超时时间
 *
 * 安全改造说明：
 *   1. 页面启动时调用 initApiKey() 获取 a/b/c/d 安全码并缓存。
 *   2. 仅正式域名 mp.infiniax.com 会对请求体做 AES-CBC 加密（Encrypt: 1），
 *      并自动解密响应体；其他域名一律不加密。
 *   3. initApiKey 应在 main.js 中于应用挂载之前 await。
 */
import Axios from 'axios'
import moment from 'moment'
import { ElMessage as Message, ElMessageBox as MessageBox } from 'element-plus'
import Env from './env'
import { byteToString } from '../util/commonality'
import { handleTokenExpired, stopTokenRefresh } from '../util/tokenManager'
import { signMd5, encryptionCBC, decryptionCBC } from '../util/crypto'
import trimRequestParams from '../util/trimRequestParams'

// ─── 环境判断 ─────────────────────────────────────────────────────────────
const ENCRYPT_HOSTS = ['mp.infiniax.com']
const enableEncrypt = ENCRYPT_HOSTS.indexOf(location.hostname) !== -1
const silentErrorCodes = ['10153008']

// ─── API 安全码缓存 ──────────────────────────────────────────────────────
// 页面刷新后模块重新执行，_code 归零；sessionStorage 作为兜底。
const _CODE_STORAGE_KEY = '__apiSecurityCode'
let _code = null           // { a, b, c, d }
let _keyReadyPromise = null  // 持有 initApiKey() 的 Promise，供请求拦截器 await

function getCode() {
  if (_code) return _code
  try {
    const s = sessionStorage.getItem(_CODE_STORAGE_KEY)
    return s ? JSON.parse(s) : null
  } catch (_) {
    return null
  }
}

/**
 * 每次页面加载时调用，从 /api/auth/getKey 获取安全码并持久化到 sessionStorage。
 * 使用原生 fetch 避免经过 Axios 拦截器（防止循环依赖）。
 *
 * 内部将 Promise 赋给 _keyReadyPromise，请求拦截器会 await 它，
 * 确保任何请求都在 key 就绪后才发出，彻底消除"刷新后偶发解密失败"的竞态。
 *
 * 建议在 main.js 中：
 *   import { initApiKey } from './servies/config'
 *   initApiKey().then(() => new Vue({ ... }))
 */
export function initApiKey() {
  _keyReadyPromise = (async () => {
    try {
      const resp = await fetch(`${Env.api}/Ug8Kl9du8B/public`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      const json = await resp.json()
      // getKey 接口 data 为 AES-CBC(Base64) 密文，固定 key/iv 解密后再解析业务 JSON
      let envelope = json
      if (json && typeof json.data === 'string' && json.data.trim()) {
        try {
          const plain = decryptionCBC(json.data, INIT_API_KEY_AES_KEY, INIT_API_KEY_AES_IV)
          if (plain) {
            envelope = JSON.parse(plain)
          }
        } catch (e) {
          console.warn('[initApiKey] 响应 data 解密失败，按明文结构解析', e)
        }
      }
      // 兼容 status:'1' 和 statusCode:200 两种格式；解密后 body 也可能直接为 { a,b,c,d }
      let payload = null
      if (envelope && (envelope.status === '1' || envelope.statusCode === 200)) {
        payload = envelope.data
      } else if (envelope && envelope.a) {
        payload = envelope
      }
      if (payload && payload.a) {
        _code = payload
        sessionStorage.setItem(_CODE_STORAGE_KEY, JSON.stringify(_code))
      }
    } catch (e) {
      console.warn('[initApiKey] 获取安全码失败，尝试使用缓存', e)
      // 使用上次成功缓存的值继续工作（兜底：sessionStorage 跨刷新持久化）
      const s = sessionStorage.getItem(_CODE_STORAGE_KEY)
      if (s) try { _code = JSON.parse(s) } catch (_) {}
    }
  })()
  return _keyReadyPromise
}

// ─── 不需要加/解密的 URL 白名单 ───────────────────────────────────────────
const IGNORE_ENCRYPT_PATTERNS = [
  '/auth/getKey',
  '/v1/file/upload2',
  '/thirdparty/ocr'
]

function isIgnoreEncryptUrl(url = '') {
  return IGNORE_ENCRYPT_PATTERNS.some(p => url.includes(p))
}

// ─── 特殊解密标识（与服务端约定） ────────────────────────────────────────
// URL 路径中含有此字符串（位置 > 0）时使用固定 key 解密
const SPECIAL_URL_FLAG = 'Vgu'
const SPECIAL_FIXED_KEY = 'ye8Ux7GPKbdXS1N1'
const SPECIAL_FIXED_IV  = 'hUGAQXoSwokKfNa2'

// initApiKey（/Ug8Kl9du8B/public）响应 data 字段 AES-CBC 解密（与服务端约定固定密钥）
const INIT_API_KEY_AES_KEY = 'rNGh1ewKxpSO8ykk'
const INIT_API_KEY_AES_IV = 'ejGRmFZ29GYYmpOP'

// ─── 获取链接地址参数 ─────────────────────────────────────────────────────
function getQueryVariable(variable) {
  const query = window.location.href
  const vars  = query.split('?')
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=')
    if (pair[0] === variable) return pair[1]
  }
  return false
}

// ─── 网关/代理返回 HTML 错误页时，避免把原始 HTML 直接展示给用户 ─────────────
function getSystemErrorMessage() {
  const lang = localStorage.LANG || 'zh-CN'
  return lang === 'en-US'
    ? 'System error, please try again later.'
    : '系统错误，请稍后重试。'
}

function isHtmlErrorMessage(message) {
  if (message == null) return false
  const text = String(message).trim()
  if (!text) return false
  const upper = text.toUpperCase()
  return (
    upper.includes('<!DOCTYPE HTML') ||
    upper.includes('<!DOCTYPE') ||
    (upper.includes('<HTML') && upper.includes('</HTML>'))
  )
}

function sanitizeErrorMessage(message) {
  return isHtmlErrorMessage(message) ? getSystemErrorMessage() : message
}

function showErrorMessage(message) {
  const safe = sanitizeErrorMessage(message)
  if (safe) Message.error(safe)
}

function showWarningMessage(message) {
  const safe = sanitizeErrorMessage(message)
  if (safe) Message({ message: safe, type: 'warning' })
}

// ─── Axios 基础配置 ───────────────────────────────────────────────────────
var flag = true
var specialCode = ['20101026', '20101014', '20101015', '20101016', '20101017', '20101019', '20101027']

Axios.defaults.timeout = 600000
Axios.defaults.headers.post['Content-Type'] = 'application/json; charset = utf-8'
Axios.defaults.withCredentials = false
Axios.defaults.baseURL = Env.api || 'http://localhost:3000'
Axios.defaults.headers.common['domain-prefix'] = 'mp'
Axios.defaults.headers.common['entry-domain']  = location.host
Axios.defaults.headers.common['FINGERPRINT']   = sessionStorage.fingerprint
Axios.defaults.headers.common['LANG']          = localStorage.LANG

// ─── 请求拦截器 ───────────────────────────────────────────────────────────
Axios.interceptors.request.use(
  async (config) => {
    config.data = trimRequestParams(config.data)
    config.params = trimRequestParams(config.params)

    // 基础鉴权头
    config.headers['domain-prefix'] = 'mp'
    config.headers['entry-domain']  = location.host

    if (location.href.includes('token=')) {
      config.headers['Authorization'] = location.href.split('token=')[1]
    } else {
      config.headers['Authorization'] =
        (sessionStorage.loginData && sessionStorage.loginData !== 'null')
          ? 'Bearer ' + JSON.parse(sessionStorage.loginData).accessToken
          : ''
    }
    config.headers['LANG'] = localStorage.LANG

    // 外链或无需加密的 URL 直接放行（不等待 key）
    if (isIgnoreEncryptUrl(config.url || '')) {
      return config
    }

    // ── 等待安全码就绪，消除"第一次刷新后 key 还没拿到就发请求"的竞态 ──────
    if (_keyReadyPromise) {
      await _keyReadyPromise
    }

    // ── 安全签名头 ────────────────────────────────────────────────────────
    const timeStamp = Date.now()
    const dateStr   = moment().format('YYYYMMDDHHmmss')

    config.headers['TimeStamp'] = timeStamp
    config.headers['DateTime']  = dateStr

    const code = getCode()
    if (code) {
      config.headers['Client-id'] = signMd5(
        code.b + code.c + code.a + code.d
      ).toUpperCase()
    }

    if (!enableEncrypt) {
      // 非正式域名（本地 / s2t / uat / pre 等）不加密
      config.headers['Encrypt'] = 0
      return config
    }

    // ── 正式域名：加密请求体 ─────────────────────────────────────────────
    config.headers['Encrypt'] = 1

    if (code && config.data && config.responseType !== 'blob') {
      const md5 = signMd5(String(dateStr - timeStamp))
      // key = a前4位 + b后4位 + b前4位 + a后4位 + md5前16位
      const key = code.a.substring(0, 4) + code.b.substring(4)
                + code.b.substring(0, 4) + code.a.substring(4)
                + md5.substring(0, 16)
      const iv  = md5.substring(16)

      config.data = { data: encryptionCBC(JSON.stringify(config.data), key, iv) }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// ─── 响应体解密工具（内部使用） ───────────────────────────────────────────
function tryDecryptResponse(response) {
  const url        = (response.config && response.config.url) || ''
  const isIgnored  = isIgnoreEncryptUrl(url)
  const isBlob     = response.config && response.config.responseType === 'blob'
  const hasPayload = response.data && typeof response.data.data === 'string'

  if (isIgnored || isBlob || !hasPayload) return response

  try {
    let plainText

    // 特殊 URL（路径中含有 SPECIAL_URL_FLAG 且不在首位）使用固定 key
    const flagIdx = url.indexOf(SPECIAL_URL_FLAG)
    if (flagIdx > 0) {
      plainText = decryptionCBC(response.data.data, SPECIAL_FIXED_KEY, SPECIAL_FIXED_IV)
    } else {
      // 动态 key：由请求时写入的 TimeStamp / DateTime 派生
      const timeStamp = response.config.headers['TimeStamp']
      const dateTime  = response.config.headers['DateTime']
      const code      = getCode()

      if (!code || !timeStamp || !dateTime) return response

      const md5 = signMd5(String(dateTime - timeStamp))
      // key = c前4位 + d前4位 + d后4位 + c后4位 + md5前16位
      const key = code.c.substring(0, 4) + code.d.substring(0, 4)
                + code.d.substring(4)    + code.c.substring(4)
                + md5.substring(0, 16)
      const iv  = md5.substring(16)

      plainText = decryptionCBC(response.data.data, key, iv)
    }

    response.data = JSON.parse(plainText)
  } catch (e) {
    console.warn('[tryDecryptResponse] 解密失败，使用原始响应', e)
  }

  return response
}

// ─── 响应拦截器 ───────────────────────────────────────────────────────────
Axios.interceptors.response.use(
  (res) => {
    // 正式域名先解密
    if (enableEncrypt) {
      res = tryDecryptResponse(res)
    }   

    if (res.status === 200 && (res.data.status === '1' || res.data instanceof Blob)) {
      return res

    } else if (res.data.byteLength) {
      // 二进制响应（旧版下载文件兼容）
      try {
        const enc  = new TextDecoder('utf-8')
        const data = JSON.parse(enc.decode(new Uint8Array(res.data)))

        if (data && data.code === '99999401') {
          if (flag) {
            flag = false
            stopTokenRefresh()
            showWarningMessage(data.message)
            sessionStorage.removeItem('loginData')
            sessionStorage.removeItem('kycStatusFlag')
            setTimeout(() => {
              window.location.href = `${window.location.origin}/#/login`
              setTimeout(() => { window.location.reload(); flag = true }, 300)
            }, 3000)
          }
        } else if (!silentErrorCodes.includes(String(data && data.code))) {
          showErrorMessage(data.message)
        }
      } catch (_) {
        return res
      }

    } else {
      // 登录过期 / 登出相关状态码
      const expiryCodes = ['401', '99999401', '10100009', '10100010', '10100016']
      const isExpired   = expiryCodes.includes(String(res.data.code)) ||
        (res.data.code === '10100018' && location.href.includes('/login'))

      if (isExpired) {
        if (flag) {
          flag = false
          stopTokenRefresh()
          showWarningMessage(res.data.message)
          sessionStorage.removeItem('loginData')
          sessionStorage.removeItem('kycStatusFlag')
          sessionStorage.removeItem('entityData')
          sessionStorage.removeItem('profile')
          sessionStorage.removeItem('firstLogin')
          setTimeout(() => {
            window.location.href = `${window.location.origin}/#/login`
            setTimeout(() => { window.location.reload(); flag = true }, 300)
          }, 2000)
        }
      } else {
        const errMsg =
          (res.data && res.data.message) ||
          (typeof res.data === 'string' ? res.data : '')
        if (errMsg && !silentErrorCodes.includes(String(res.data && res.data.code))) {
          showErrorMessage(errMsg)
        }
        if (flag) return Promise.reject(res)
      }
    }
  },

  (err) => {
    console.log(err, 'err🚀----', err.response && err.response.status, err.response)

    if (Axios.isCancel(err)) {
      return Promise.reject(Object.assign({}, err, { isCancel: true }))
    }

    if (err.message && err.message.includes('timeout')) {
      showErrorMessage(err.message)
    }

    if (err && err.response) {
      const configMessage = (type) => {
        const map = {
          '404': { 'zh-CN': '请求超时，请检查网络并刷新页面重试。',         'en-US': 'Time out, please check the network and refresh the page' },
          '408': { 'zh-CN': '请求连接超时，请检查网络并刷新页面重试。',     'en-US': 'Connection time out, please check the network and refresh the page' },
          '500': { 'zh-CN': '处理失败，请稍后重试。',                       'en-US': 'Processing failed, please try again later' },
          '502': { 'zh-CN': '处理失败，请稍后重试。',                       'en-US': 'Processing failed, please try again later' },
          '503': { 'zh-CN': '处理失败，请稍后重试。',                       'en-US': 'Processing failed, please try again later' },
          '504': { 'zh-CN': '请求网关超时，请检查网络并刷新页面重试。',     'en-US': 'gateway requested failed, please check the network and refresh the page' },
        }
        return (map[type] || {})[localStorage.LANG] || ''
      }

      const responseData = err.response.data
      const rawMessage =
        (responseData && responseData.message) ||
        (typeof responseData === 'string' ? responseData : '')

      switch (err.response.status) {
        case 404: Message.error(configMessage('404')); break
        case 408: Message.error(configMessage('408')); break
        case 500: Message.error(configMessage('500')); break
        case 502: Message.error(configMessage('502')); break
        case 503: Message.error(configMessage('503')); break
        case 504: Message.error(configMessage('504')); break
        case 401: handleTokenExpired(); break
        default:
          if (isHtmlErrorMessage(rawMessage)) {
            Message.error(getSystemErrorMessage())
          }
          break
      }
    }

    if (flag) return Promise.reject(err)
  }
)

export default Axios
